// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { InstrumentAllowance } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/TransferPreapproval';
import { DataTable } from '../../../components/Table/DataTable';
import { TransferPreapprovalDialog } from './TransferPreapprovalDialog';
import { createTransferPreapprovalColumns } from './createTransferPreapprovalColumns';
import { useTransferPreapprovalTable } from './useTransferPreapprovalTable';

interface TransfersPreapprovalsProps {
  disableVirtualization?: boolean;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}

export const TransfersPreapprovals: React.FC<TransfersPreapprovalsProps> = ({
  disableVirtualization,
  dialogOpen,
  setDialogOpen,
}) => {
  // Dynamic row height for preapprovals table
  const getPreapprovalRowHeight = (params: {
    model: {
      payload?: {
        instrumentAllowances?: Array<InstrumentAllowance>;
      };
    };
  }) => {
    const allowances = params.model?.payload?.instrumentAllowances ?? [];
    if (!Array.isArray(allowances) || allowances.length === 0) return 52;
    return 32 + allowances.length * 24;
  };

  const {
    rows: preapprovalRows,
    modifyContract: modifyTransferPreapprovalContract,
    setModifyContract: setModifyPreapprovalContract,
    actions: preapprovalActions,
  } = useTransferPreapprovalTable();

  return (
    <>
      <TransferPreapprovalDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        mode="create"
      />
      {modifyTransferPreapprovalContract && (
        <TransferPreapprovalDialog
          open
          modifyContract={modifyTransferPreapprovalContract}
          onClose={() => setModifyPreapprovalContract(null)}
          mode="modify"
        />
      )}
      <DataTable
        disableVirtualization={disableVirtualization}
        rows={preapprovalRows}
        rowKey={(a) => a.contractId}
        columns={createTransferPreapprovalColumns(
          preapprovalRows,
          preapprovalActions,
          setModifyPreapprovalContract,
        )}
        getRowHeight={getPreapprovalRowHeight}
      />
    </>
  );
};
